import {
  CampaignType,
  redirectAbsolute,
  redirectCommon,
  redirectServiceToService,
  redirectViaCampaignType,
} from '@corefront/redux-aba';
import { useFeature } from '@growthbook/growthbook-react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { LINK_TYPES } from '../AppSCCOM/constants/linkTypes';
import { FEATURES } from '../constants/featureToggling';
import { deeplinkProtocolSelector } from '../redux/reducers/app/configuration';
import { PRODUCT_NAMES_MAP } from '../constants/productTypes';
import { brandHashSelector } from '../redux/reducers/app/params';

// additional param for deposit campaign
// https://jira.pm.tech/browse/GAM-12461
export const SOURCE_TYPE_CASINO_HEADER = 'casino_header';
export const SOURCE_TYPE_CASINO_BONUSES = 'casino_bonuses';

export const useDeeplinkProtocol = () => {
  const defaultProtocol = useSelector(deeplinkProtocolSelector);
  const brandHash = useSelector(brandHashSelector);

  if (!brandHash) {
    return defaultProtocol;
  }

  return `app-${brandHash}://`;
};

export const useRedirectViaCampaignType = () => {
  const dispatch = useDispatch();
  const protocol = useDeeplinkProtocol();

  return useCallback(
    ({ to, param, permanent, channel }) => {
      const action = redirectViaCampaignType({
        to,
        param,
        channel,
        permanent,
        protocol,
      });

      dispatch(action);
    },
    [dispatch, protocol]
  );
};

export const useServiceToServiceRedirect = () => {
  const dispatch = useDispatch();
  const protocol = useDeeplinkProtocol();

  return useCallback(
    ({ to, permanent, channel }) => {
      const action = redirectServiceToService({
        to,
        channel,
        permanent,
        protocol,
      });

      dispatch(action);
    },
    [dispatch, protocol]
  );
};

export const useRedirectAbsolute = () => {
  const dispatch = useDispatch();
  const protocol = useDeeplinkProtocol();

  return useCallback(
    ({ to, permanent, channel }) => {
      const action = redirectAbsolute({
        to,
        channel,
        permanent,
        protocol,
      });

      dispatch(action);
    },
    [dispatch, protocol]
  );
};

export const useRedirectToCashier = (
  source_type = SOURCE_TYPE_CASINO_HEADER
) => {
  const redirectViaCampaignType = useRedirectViaCampaignType();
  const isEnabledOneClickDeposit = useFeature(
    FEATURES.ENABLED_ONE_CLICK_DEPOSIT
  ).on;

  const param = JSON.stringify({
    source_type,
    ...(isEnabledOneClickDeposit ? { isOneClickSupported: true } : {}),
  });

  return useCallback(() => {
    redirectViaCampaignType({
      to: CampaignType.Deposit,
      param,
    });
  }, [redirectViaCampaignType, param]);
};

export const useDeeplinkRedirectCallBack = () => {
  const dispatch = useDispatch();
  const protocol = useDeeplinkProtocol();

  return useCallback(
    (deeplink) => () => {
      try {
        const url = new URL(deeplink);
        const searchParams = url.searchParams;

        const campaign_type = searchParams.get('campaign_type');
        const campaign_param = searchParams.get('campaign_param');
        const campaign_permanent = searchParams.get('campaign_permanent');

        const action = redirectCommon({
          protocol,
          channel: 'nativeapp',
          campaign_type,
          campaign_param,
          campaign_permanent,
        });

        dispatch(action);
      } catch (e) {
        console.info('Deep link search params are not valid');
      }
    },
    [dispatch, protocol]
  );
};

export const useDeeplinkRedirect = () => {
  const redirectServiceToService = useServiceToServiceRedirect();

  const redirect = useCallback(
    (productType, path) => {
      redirectServiceToService({
        to: {
          path,
          'service-name': PRODUCT_NAMES_MAP[productType],
        },
      });
    },
    [redirectServiceToService]
  );

  return redirect;
};

export const useRedirectViaLinkType = () => {
  const history = useHistory();

  const serviceToServiceRedirect = useDeeplinkRedirect();
  const serviceToServiceCallback = useCallback(
    (uriPath, service) => () => serviceToServiceRedirect(service, uriPath),
    [serviceToServiceRedirect]
  );
  const deeplinkRedirect = useDeeplinkRedirectCallBack();
  const internalRedirect = useCallback(
    (uriPath) => () => {
      history.push(uriPath);
    },
    [history]
  );

  return useCallback(
    (linkType) => {
      switch (linkType) {
        case LINK_TYPES.DEEPLINK:
          return deeplinkRedirect;

        case LINK_TYPES.INTERNAL:
          return internalRedirect;

        case LINK_TYPES.SERVICE:
        default:
          return serviceToServiceCallback;
      }
    },
    [deeplinkRedirect, serviceToServiceCallback, internalRedirect]
  );
};

export const useServiceRedirectCallback = () => {
  const dispatch = useDispatch();
  const protocol = useDeeplinkProtocol();

  return useCallback(
    (productType, path) => () => {
      const action = redirectServiceToService({
        protocol,
        to: {
          path,
          'service-name': PRODUCT_NAMES_MAP[productType],
        },
      });

      dispatch(action);
    },
    [dispatch, protocol]
  );
};
